import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Utils, authJsonHeaders, getSafeRedirectPath, hasCompletedOnboarding, resolvePostLoginRedirect, isStaffUser, resolveStaffPostLoginPath } from '../../utils';
import { useAuth } from './AuthContext';
import './signup.css'
import eyeOpenImg from "../../assets/icons/eye.png"
import eyeCloseImg from "../../assets/icons/invisible.png"
import logo from "../../assets/notifysphere-logo.png"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import googleImg from "../../assets/icons/google.svg"
import TwoFactorVerify from './TwoFactorVerify';
import TwoFactorSetup from './TwoFactorSetup';


function NewSignIn({ handleSaveToken, handleSetOnboard, handleChangeSidebar, handleSetAppId }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState("")
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = getSafeRedirectPath(searchParams);

  const navigateAfterLogin = (userData) => {
    if (isStaffUser(userData)) {
      handleSetOnboard();
      const staffPath = resolveStaffPostLoginPath(userData);
      const appMatch = staffPath.match(/^\/apps\/([^/]+)/);
      if (appMatch?.[1]) {
        handleChangeSidebar('dashboardSidebar', 0);
        handleSetAppId(appMatch[1]);
      }
      navigate(staffPath);
      return;
    }

    const planResumePath = resolvePostLoginRedirect(redirectPath);

    if (planResumePath === "/payment" || planResumePath === "/plan") {
      navigate(planResumePath);
      return;
    }

    if (!hasCompletedOnboarding(userData)) {
      navigate(`/steps`);
      return;
    }
    handleSetOnboard();
    if (redirectPath) {
      navigate(redirectPath);
      return;
    }
    navigate(`/apps`);
  };

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [pending2FAUserId, setPending2FAUserId] = useState(null);
  const [pending2FAEmail, setPending2FAEmail] = useState('');
  /** Short-lived JWT from signin — required for /2fa/* during login */
  const [pending2FAToken, setPending2FAToken] = useState(null);
  /** First login after migration: server issues recovery codes once — show before redirect */
  const [recoveryCodesModal, setRecoveryCodesModal] = useState(null);

  function handleSignIn(e) {
    e.preventDefault();
    let valid = true;
    setEmailError('');
    setPasswordError('');
    setIsLoading(true)

    // Basic email and password validation
    if (!email) {
      setIsLoading(false)
      setEmailError('Email is required');
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setIsLoading(false)
      setEmailError('Please enter a valid email address');
      valid = false;
    }

    if (!password) {
      setIsLoading(false)
      setPasswordError('Password is required');
      valid = false;
    } else if (password.length < 6) {
      setIsLoading(false)
      setPasswordError('Password should be at least 6 characters');
      valid = false;
    }

    if (!valid) {
      return;
    }

    let data = {
      email: email.trim(),
      password: password,
    };

    fetch(Utils.baseurl + 'signin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then(async (result) => {
        if (result.status) {
          if (result.requires2FA) {
            setPending2FAUserId(result.userId);
            setPending2FAEmail(result.email);
            setPending2FAToken(result.pending2FAToken || null);

            if (result.needs2FASetup) {
              setShow2FASetup(true);
            } else {
              setShow2FA(true);
            }
            setIsLoading(false);
            return;
          }

          handleSaveToken(result.accessToken, JSON.stringify(result.data), result.refreshToken)
          signIn(result.accessToken)
          navigateAfterLogin(result.data);
          setIsLoading(false);
          return;
        } else {
          setFormError(result.message)
          setIsLoading(false)
        }
      })
      .catch((error) => {
        setIsLoading(false)
        console.error(error)
      });
  }

  const finish2FALoginAfterVerify = () => {
    setShow2FA(false);
    setPending2FAUserId(null);
    setPending2FAEmail('');
    try {
      const stored = JSON.parse(localStorage.getItem("userData") || "{}");
      navigateAfterLogin(stored);
    } catch (_) {
      navigate(`/apps`);
    }
  };

  const handle2FASuccess = (result) => {
    handleSaveToken(result.accessToken, JSON.stringify(result.data), result.refreshToken);
    signIn(result.accessToken);

    if (result.recoveryCodesIssued && result.recoveryCodesIssued.length) {
      setRecoveryCodesModal(result.recoveryCodesIssued);
      return;
    }

    finish2FALoginAfterVerify();
  };

  const closeRecoveryCodesModal = () => {
    setRecoveryCodesModal(null);
    finish2FALoginAfterVerify();
  };

  const handle2FACancel = () => {
    setShow2FA(false);
    setShow2FASetup(false);
    setPending2FAUserId(null);
    setPending2FAEmail('');
    setPending2FAToken(null);
    setPassword('');
  };

  const handle2FASetupComplete = () => {
    setShow2FASetup(false);
    setShow2FA(true);
  };

  const handle2FASkip = async () => {
    setFormError("");
    setIsLoading(true);
    try {
      const resp = await fetch(Utils.baseurl + '2fa/skip', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.token, pending2FAToken),
        body: JSON.stringify({ userId: pending2FAUserId }),
      });
      const result = await resp.json();
      if (result.status) {
        handleSaveToken(result.accessToken, JSON.stringify(result.data), result.refreshToken);
        signIn(result.accessToken);
        setShow2FASetup(false);
        navigateAfterLogin(result.data);
      } else {
        setFormError(result.message || 'Could not skip two-factor authentication');
      }
    } catch (err) {
      console.error(err);
      setFormError('Could not skip two-factor authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    setFormError("");
    setEmailError("")
  }, [email]);

  useEffect(() => {
    setFormError("");
    setPasswordError("")
  }, [password]);

  const handleGoogleSignIn = (e) => {
    e.preventDefault();

    if (!window.google || !window.google.accounts || !window.google.accounts.oauth2) {
      setFormError('Google Sign-In is not available. Please try again later.');
      return;
    }

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: 'email profile openid',
      ux_mode: 'popup',
      callback: async (response) => {
        if (response.error) {
          setFormError('Google Sign-In was cancelled or failed.');
          return;
        }

        try {
          setIsLoading(true);
          const resp = await fetch(Utils.baseurl + 'auth/google/code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: response.code }),
          });
          const result = await resp.json();

          if (result.status) {
            handleSaveToken(result.accessToken, JSON.stringify(result.data), result.refreshToken);
            signIn(result.accessToken);
            navigateAfterLogin(result.data);
          } else {
            setIsLoading(false);
            setFormError(result.message || 'Google sign-in failed');
          }
        } catch (err) {
          setIsLoading(false);
          setFormError('Google sign-in failed. Please try again.');
          console.error(err);
        }
      },
    });

    client.requestCode();
  };

  if (show2FASetup) {
    return (
      <TwoFactorSetup
        userId={pending2FAUserId}
        pending2FAToken={pending2FAToken}
        onComplete={handle2FASetupComplete}
        onCancel={handle2FACancel}
        onSkip={handle2FASkip}
        isMandatory={true}
      />
    );
  }

  if (show2FA) {
    return (
      <>
        <TwoFactorVerify
          userId={pending2FAUserId}
          email={pending2FAEmail}
          pending2FAToken={pending2FAToken}
          onSuccess={handle2FASuccess}
          onCancel={handle2FACancel}
        />
        {recoveryCodesModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.65)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="recovery-codes-title"
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                maxWidth: 420,
                width: "100%",
                padding: 28,
                boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
                textAlign: "left",
              }}
            >
              <h2 id="recovery-codes-title" style={{ margin: "0 0 12px", fontSize: 20 }}>
                Save your recovery codes
              </h2>
              <p style={{ margin: "0 0 16px", color: "#475569", fontSize: 14, lineHeight: 1.5 }}>
                Your account had no recovery codes stored. We generated new one-time codes — save them
                somewhere safe. Each works once if you lose your authenticator app.
              </p>
              <pre
                style={{
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 14,
                  fontSize: 13,
                  lineHeight: 1.6,
                  overflow: "auto",
                  maxHeight: 200,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {recoveryCodesModal.join("\n")}
              </pre>
              <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(recoveryCodesModal.join("\n"));
                  }}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Copy all
                </button>
                <button
                  type="button"
                  onClick={closeRecoveryCodesModal}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    border: "none",
                    background: "#4332ce",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Continue to app
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="signup_page">
      <div className="signup_page_inner">
        <div className="page_logo">
          <a href="/">
            <img src={logo} alt="notifySphere" />
          </a>
        </div>
        <div className="form_outer">
          <div className="text-center">
            <div className="form_heading">
              <h1 color="black">Get Started Today</h1>
              <p style={{ color: "black" }}>{"Quick setup, no credit card required."}</p>
            </div>
          </div>
          {formError && <div className='alert alert-danger'>
            <span>{formError}</span>
          </div>}

          <div className="login_form">
            <div id="googleButtonDivSign" className="signup_google" style={{ marginBottom: 16 }}>
              <button className="signup_google_button" onClick={handleGoogleSignIn}>
                <img src={googleImg} alt="google" />
                <span className="">Sign in with Google</span>
              </button>
            </div>
            <form onSubmit={handleSignIn}>
              <div className="input_boxes">
                <div className="email_box">
                  <label htmlFor="field_email"> Email Address</label>
                  <div className="email_input">
                    <input id="field_email" onChange={(e) => setEmail(e.target.value)} spellCheck="false" />
                  </div>
                  {emailError && <span style={{ color: "#ff0000d1" }}>{emailError}</span>}
                </div>
                <div className="password_box">
                  <label htmlFor="field_password">Password</label>
                  <div className="email_input password_input">
                    <input id="field_password" type={showPassword ? "text" : "password"} onChange={(e) => setPassword(e.target.value)} />
                    <img
                      src={showPassword ? eyeCloseImg : eyeOpenImg}
                      alt="Toggle Password"
                      onClick={toggleShowPassword}
                    />
                  </div>
                  {passwordError && <span style={{ color: "#ff0000d1" }}>{passwordError}</span>}
                </div>
              </div>
              <div className="recaptcha">
                <input id="recaptcha" type="checkbox" className="checkbox" />
                <div className="recaptcha_lable">
                  <label htmlFor="recaptcha" className="sc-dwLEzm jwPkpm">
                    <span>
                      <span color="dark">Stay logged in</span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="signup_button">
                <button style={{ backgroundColor: isLoading ? "#686abd" : "rgb(67, 70, 206)" }} disabled={isLoading} type='submit'>
                  {isLoading ? <FontAwesomeIcon icon={faSpinner} spin size="1x" /> : <span>Sign in</span>}
                </button>
              </div>
            </form>
            <div className="already_acc">
              <span color="dark">Not have an account? <Link to="/signUp">Signup</Link></span>
              <span color="dark"><Link to="/forgot-password">Forgot password?</Link></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewSignIn;
